import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';

const FONT = "'Plus Jakarta Sans', sans-serif";

const COLORS = {
    bg: '#0f172a',
    card: '#1e293b',
    cardHover: '#334155',
    border: '#334155',
    text: '#f8fafc',
    muted: '#94a3b8',
    green: '#22c55e',
    greenBg: 'rgba(34, 197, 94, 0.1)',
    greenBorder: 'rgba(34, 197, 94, 0.3)',
    amber: '#f59e0b',
    amberBg: 'rgba(245, 158, 11, 0.1)',
    input: '#0f172a',
    inputBorder: '#475569',
    inputFocus: '#22c55e',
};

/**
 * DenominationCounter - Reusable denomination input grid for cash counting.
 *
 * Props:
 *   denominations  - Array of { value: number, type: 'note' | 'coin' }
 *   currency       - Currency symbol string (e.g. '£')
 *   onTotalChange  - Callback invoked with the running total whenever quantities change
 */
const DenominationCounter = ({ denominations = [], currency = '£', onTotalChange }) => {
    const { t } = useLanguage();

    const [quantities, setQuantities] = useState(() =>
        denominations.reduce((acc, d) => ({ ...acc, [d.value]: 0 }), {})
    );
    const [focusedRow, setFocusedRow] = useState(null);

    const total = useMemo(() => {
        return denominations.reduce((sum, d) => sum + d.value * (quantities[d.value] || 0), 0);
    }, [denominations, quantities]);

    // Notify parent of total changes via useEffect (avoids setState-during-render warning)
    useEffect(() => {
        if (onTotalChange) onTotalChange(total, quantities);
    }, [total, quantities, onTotalChange]);

    const handleQuantityChange = useCallback(
        (value, qty) => {
            const parsed = Math.max(0, parseInt(qty, 10) || 0);
            setQuantities((prev) => ({ ...prev, [value]: parsed }));
        },
        []
    );

    const handleIncrement = useCallback(
        (value) => {
            setQuantities((prev) => ({ ...prev, [value]: (prev[value] || 0) + 1 }));
        },
        []
    );

    const handleDecrement = useCallback(
        (value) => {
            setQuantities((prev) => {
                const current = prev[value] || 0;
                if (current <= 0) return prev;
                return { ...prev, [value]: current - 1 };
            });
        },
        []
    );

    if (!denominations.length) {
        return (
            <div style={{ padding: 24, textAlign: 'center', color: COLORS.muted, fontFamily: FONT }}>
                {t('no_denominations_configured') || 'No denominations configured'}
            </div>
        );
    }

    return (
        <div style={{ fontFamily: FONT }}>
            {/* Header row */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '48px 1fr 80px 120px 100px',
                    gap: 8,
                    padding: '8px 12px',
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    color: COLORS.muted,
                }}
            >
                <span />
                <span>{t('denomination') || 'Denomination'}</span>
                <span>{t('type') || 'Type'}</span>
                <span style={{ textAlign: 'center' }}>{t('quantity') || 'Qty'}</span>
                <span style={{ textAlign: 'right' }}>{t('subtotal') || 'Subtotal'}</span>
            </div>

            {/* Denomination rows */}
            {denominations.map((d) => {
                const qty = quantities[d.value] || 0;
                const subtotal = d.value * qty;
                const isNote = d.type === 'note';
                const isFocused = focusedRow === d.value;
                const iconColor = isNote ? COLORS.green : COLORS.amber;
                const iconBg = isNote ? COLORS.greenBg : COLORS.amberBg;

                return (
                    <div
                        key={d.value}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '48px 1fr 80px 120px 100px',
                            gap: 8,
                            alignItems: 'center',
                            padding: '10px 12px',
                            borderRadius: 8,
                            marginBottom: 4,
                            background: isFocused ? COLORS.cardHover : COLORS.card,
                            border: `1px solid ${isFocused ? COLORS.inputFocus : COLORS.border}`,
                            transition: 'all 0.15s ease',
                        }}
                    >
                        {/* Icon */}
                        <div
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: iconBg,
                                fontSize: 16,
                            }}
                        >
                            <span style={{ color: iconColor }}>{isNote ? '\uD83D\uDCB5' : '\uD83E\uDE99'}</span>
                        </div>

                        {/* Value */}
                        <span style={{ color: COLORS.text, fontWeight: 600, fontSize: 15 }}>
                            {currency}{d.value.toFixed(2)}
                        </span>

                        {/* Type badge */}
                        <span
                            style={{
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                color: iconColor,
                                background: iconBg,
                                padding: '3px 8px',
                                borderRadius: 4,
                                textAlign: 'center',
                            }}
                        >
                            {isNote ? (t('note') || 'Note') : (t('coin') || 'Coin')}
                        </span>

                        {/* Quantity controls */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <button
                                onClick={() => handleDecrement(d.value)}
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 6,
                                    border: `1px solid ${COLORS.border}`,
                                    background: COLORS.bg,
                                    color: COLORS.text,
                                    fontSize: 16,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                -
                            </button>
                            <input
                                type="number"
                                min="0"
                                value={qty}
                                onChange={(e) => handleQuantityChange(d.value, e.target.value)}
                                onFocus={() => setFocusedRow(d.value)}
                                onBlur={() => setFocusedRow(null)}
                                style={{
                                    width: 48,
                                    height: 32,
                                    borderRadius: 6,
                                    border: `1px solid ${isFocused ? COLORS.inputFocus : COLORS.inputBorder}`,
                                    background: COLORS.input,
                                    color: COLORS.text,
                                    textAlign: 'center',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    outline: 'none',
                                    fontFamily: FONT,
                                }}
                            />
                            <button
                                onClick={() => handleIncrement(d.value)}
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 6,
                                    border: `1px solid ${COLORS.greenBorder}`,
                                    background: COLORS.greenBg,
                                    color: COLORS.green,
                                    fontSize: 16,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                +
                            </button>
                        </div>

                        {/* Subtotal */}
                        <span
                            style={{
                                textAlign: 'right',
                                fontWeight: 700,
                                fontSize: 14,
                                color: subtotal > 0 ? COLORS.green : COLORS.muted,
                            }}
                        >
                            {currency}{subtotal.toFixed(2)}
                        </span>
                    </div>
                );
            })}

            {/* Running total bar */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 12,
                    padding: '14px 16px',
                    borderRadius: 10,
                    background: COLORS.greenBg,
                    border: `1px solid ${COLORS.greenBorder}`,
                }}
            >
                <span style={{ color: COLORS.green, fontWeight: 700, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    {t('total_counted') || 'Total Counted'}
                </span>
                <span style={{ color: COLORS.green, fontWeight: 800, fontSize: 22 }}>
                    {currency}{total.toFixed(2)}
                </span>
            </div>
        </div>
    );
};

export default DenominationCounter;
